// 作品テーマに左右されず、すべての新規ゲームで必要になる基本アイテムの保証。
// LLM 出力やテンプレートの内容に依存させず、キャラ生成・ショップ生成・既存案件移行の
// 各経路から同じ純粋関数を使用する。

export const TREASURE_CHEST_COMPASS_ITEM_ID = 'treasure_chest_compass';
export const POTION_ITEM_ID = 'potion';
/** MP 回復の基本アイテム（共有マスタ items.json に常駐する既定アイテム）。 */
export const MP_RECOVERY_ITEM_ID = 'minor_magic_herb';

export const ESSENTIAL_INITIAL_ITEM_MINIMUMS = Object.freeze({
    [TREASURE_CHEST_COMPASS_ITEM_ID]: 1,
    [POTION_ITEM_ID]: 10,
});

export const ESSENTIAL_SHOP_ENTRIES = Object.freeze([
    Object.freeze({
        itemId: TREASURE_CHEST_COMPASS_ITEM_ID,
        buyPrice: 15000,
        sortOrder: 55,
    }),
    // ②スキャフォールドは新規ゲームのショップを初期化するため、これが無いと MP 回復手段を
    // 一切買えない状態で始まる（序盤にスキルを撃つと詰む・2026-07-26 指示）
    Object.freeze({
        itemId: MP_RECOVERY_ITEM_ID,
        buyPrice: 200,
        sortOrder: 10,
    }),
]);

const normalizedCount = (value) => {
    const count = Number(value);
    return Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0;
};

/** 既存の多い所持数は維持しつつ、全キャラの必須初期所持数を下限保証する。 */
export function withEssentialInitialItems(initialItems = null) {
    const result = initialItems && typeof initialItems === 'object' && !Array.isArray(initialItems)
        ? { ...initialItems }
        : {};
    for (const [itemId, minimum] of Object.entries(ESSENTIAL_INITIAL_ITEM_MINIMUMS)) {
        result[itemId] = Math.max(minimum, normalizedCount(result[itemId]));
    }
    return result;
}

/** キャラクターマスタを破壊的に補正し、変更したキャラ数を返す。 */
export function ensureEssentialCharacterInitialItems(document) {
    let updated = 0;
    for (const character of Array.isArray(document?.characters) ? document.characters : []) {
        const before = JSON.stringify(character.initialItems ?? null);
        const next = withEssentialInitialItems(character.initialItems);
        if (JSON.stringify(next) === before) continue;
        character.initialItems = next;
        updated++;
    }
    return updated;
}

/**
 * 無条件販売の必須商品を先頭へ合成する。同じ itemId の古い条件付き定義は除去し、
 * 時間帯・購入上限・物語テーマに左右されない販売状態を保証する。
 */
export function withEssentialShopEntries(entries = null) {
    const requiredIds = new Set(ESSENTIAL_SHOP_ENTRIES.map((entry) => entry.itemId));
    const remaining = (Array.isArray(entries) ? entries : [])
        .filter((entry) => !requiredIds.has(entry?.itemId))
        .map((entry) => ({ ...entry }));
    return [
        ...ESSENTIAL_SHOP_ENTRIES.map((entry) => ({ ...entry })),
        ...remaining,
    ];
}

/**
 * 既存 shops.json の全プロファイルへ必須商品を補完する。
 * エリア別プロファイルのどれが選ばれても購入できるよう、先頭1件だけには限定しない。
 */
export function ensureEssentialShopAvailability(document, mapId = '') {
    if (!document || typeof document !== 'object' || Array.isArray(document)) return 0;
    if (!Array.isArray(document.shopProfiles)) document.shopProfiles = [];
    if (document.shopProfiles.length === 0) {
        document.shopProfiles.push({
            id: `${mapId || 'start_map'}_essential_shop`,
            enabledAreaTypes: [],
            commonPoolIds: [],
            exclusiveEntries: [],
            midnightEntries: [],
            blackmarketEntries: [],
            eventLimitedEntries: [],
        });
    }
    let updated = 0;
    for (const profile of document.shopProfiles) {
        const before = JSON.stringify(profile.exclusiveEntries ?? null);
        const next = withEssentialShopEntries(profile.exclusiveEntries);
        if (JSON.stringify(next) === before) continue;
        profile.exclusiveEntries = next;
        updated++;
    }
    return updated;
}
